/** MV11 page adapter: hold the mover compile and Fortitude before any face can draw. */
import { CLIMB_FORTITUDE, CLIMB_FORTUNATE, resolveClimbChecks } from '../janus-engine/movement/climb-walk.js';
import { browserRollSource } from '../janus-engine/actions/roll-source.js';
import { abilityTruthReaders } from './ability-truth.js';
import { fetchAndCacheObject } from './object-cache.js';
import { actorEncounterFor, actorRowFor, registerClimbWalkPlanner } from './movement-truth.js';
type Row = Record<string, unknown>;
const row = (v: unknown): Row | null => v !== null && typeof v === 'object' && !Array.isArray(v) ? v as Row : null;
const upper = (v: unknown): string => String(Array.isArray(v) ? v[0] : v ?? '').trim().toUpperCase();
function definition(encounter: Row, mover: Row | null, id: string): Row | null {
  // A name/type shell must not mask a complete prefetched copy.
  for (const d of [row(row(mover?.catalog)?.[upper(id)]), row(row(encounter.catalog)?.[upper(id)]),
    row(window.FateTide?.objectCache?.get(upper(id)))]) if (d && row(d.items)) return d;
  return null;
}
const flights = new Map<string, Promise<unknown>>();
async function prefetch(encounter: Row, mover: Row | null = null): Promise<void> {
  // climbEntryCheck explicitly selects Fortitude; its ascension check reads that same definition.
  // MV11 S3: a natural 20 grants Fortunate — its definition is read by the roll engine too.
  await Promise.all([CLIMB_FORTITUDE, CLIMB_FORTUNATE].map(id => {
    if (definition(encounter, mover, id)) return null;
    let flight = flights.get(id);
    if (!flight) { flight = fetchAndCacheObject(id).catch(() => null).finally(() => flights.delete(id)); flights.set(id, flight); }
    return flight;
  }));
}
registerClimbWalkPlanner({ plan(encounterId, actorId, faces) {
  const encounter = actorEncounterFor(encounterId) as Row | null;
  const mover = actorRowFor(encounterId, actorId) as Row | null;
  if (!encounter || !mover) return { kind: 'handback', reason: 'climb:not-held' };
  if (!row(row(mover.computed)?.creature) || !row(mover.item_layer_types))
    return { kind: 'handback', reason: 'climb:mover-compile-not-held' };
  if (!definition(encounter, mover, CLIMB_FORTITUDE) || !definition(encounter, mover, CLIMB_FORTUNATE)) {
    void prefetch(encounter, mover);
    return { kind: 'handback', reason: 'climb:definition-not-held' };
  }
  const members = new Map<string, Row>();
  for (const r of Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr : []) {
    const member = row(r), id = upper(row(member?.encounter_creatures)?.encounter_creature_id);
    if (member && id) members.set(id, member);
  }
  members.set(upper(actorId), mover);
  const read = abilityTruthReaders(mover, encounter, new Set([upper(actorId)]));
  return resolveClimbChecks({ encounter, members, actorId, faces,
    read: { ...read, definition: id => definition(encounter, mover, id) ?? read.definition(id) } }, browserRollSource());
} });
const sweep = (): void => { for (const e of window.FateTide?.encounterCache?.values() ?? []) { const r = row(e); if (r) void prefetch(r); } };
for (const name of ['ft:encounter-state-received', 'ft:encounter-installed', 'ft:encounter-rows-patched'])
  window.addEventListener(name, e => { const enc = row((e as CustomEvent).detail?.encounterArr); if (enc) void prefetch(enc); else sweep(); });
sweep();
setTimeout(sweep, 2000);
setTimeout(sweep, 8000);
